// Traduz um erro de upload de vídeo (axios) numa mensagem clara pro usuário.
//
// Motivação: a UI mostrava sempre "Erro ao enviar vídeo" para qualquer falha
// (`catch { showToast(...) }`), o que escondeu por completo o motivo real de um
// upload de 400 MB que falhava em produção — era o timeout de 30 s do axios,
// e não havia como saber olhando a tela.
//
// O backend devolve 400 `{error: ...}` em video_upload_views.VideoUploadView;
// o axios sinaliza timeout via code ECONNABORTED e queda de rede via ERR_NETWORK.

interface AxiosLikeError {
  code?: string;
  name?: string;
  message?: string;
  status?: number;
  response?: { status?: number; data?: { error?: string } };
}

/**
 * Erro cuja `message` já está pronta para o usuário — não passa pelo mapeamento
 * genérico. Sem este marcador, ou o mapeador engoliria mensagens boas ("o vídeo
 * subiu, a publicação está demorando"), ou vazaria texto técnico na tela.
 */
export class UserFacingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UserFacingError';
  }
}

/**
 * Formata um teto de tamanho para exibição. GB inteiro sai sem decimal
 * ("2 GB", não "2.0 GB") — com o upload direto, tetos em GB deixaram de ser
 * exceção e viraram a copy padrão da tela.
 */
export function formatVideoSize(bytes: number): string {
  if (bytes >= 1024 ** 3) {
    const gb = bytes / 1024 ** 3;
    return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
  }
  return `${Math.round(bytes / 1024 ** 2)} MB`;
}

export function videoUploadErrorMessage(fileName: string, err: unknown, maxBytes: number): string {
  const e = (err ?? {}) as AxiosLikeError;

  if (err instanceof UserFacingError) return err.message;

  // Cancelamento não é falha: o usuário mandou parar.
  if (e.name === 'AbortedError') return 'Envio cancelado.';

  // Erro de transporte do envio direto (xhrPut), que não tem shape de axios.
  if (e.name === 'NetworkError') {
    return `A conexão caiu durante o envio de "${fileName}". O que já subiu foi preservado — dá para retomar de onde parou.`;
  }

  if (e.code === 'ECONNABORTED' || /timeout/i.test(e.message ?? '')) {
    return `O envio de "${fileName}" passou do tempo limite. Vídeos muito grandes ainda não são suportados por este caminho — tente um arquivo menor ou mais comprimido.`;
  }

  if (e.code === 'ERR_NETWORK' || /network/i.test(e.message ?? '')) {
    return `A conexão caiu durante o envio de "${fileName}". Nada foi salvo — verifique a internet e tente de novo.`;
  }

  // `e.status` cobre o HttpError do envio direto; `e.response.status`, o axios.
  const status = e.response?.status ?? e.status;
  const code = e.response?.data?.error;

  if (code === 'direct_upload_disabled') {
    return 'O envio de vídeos está temporariamente indisponível. Tente novamente em alguns minutos.';
  }
  if (code === 'too_many_pending_bytes') {
    return 'Você tem envios demais em andamento. Aguarde os atuais terminarem antes de começar outro.';
  }
  if (code === 'too_many_recent_failures') {
    return 'Vários envios falharam seguidamente. Aguarde alguns minutos antes de tentar de novo.';
  }
  if (code === 'session_expired') {
    return `O envio de "${fileName}" expirou. Selecione o arquivo novamente para recomeçar.`;
  }
  if (code === 'storage_unavailable') {
    return 'O armazenamento está indisponível no momento. Tente novamente em alguns minutos.';
  }
  if (code === 'file_empty' || code === 'invalid_size') {
    return `"${fileName}" parece estar vazio ou corrompido.`;
  }

  if (status === 401) {
    return 'Sua sessão expirou durante o envio. Recarregue a página e tente novamente.';
  }
  if (status === 403) {
    return 'Você não tem permissão para enviar vídeo nesta lição.';
  }
  if (status === 404) {
    // O backend revalida a permissão a cada renovação de credencial: quem foi
    // despromovido, desativado ou trocado de setor cai aqui no meio do envio.
    return 'Esta lição não está mais disponível para você editar. Recarregue a página.';
  }
  if (status === 413 || code === 'file_too_large') {
    return `"${fileName}" excede o tamanho máximo de ${formatVideoSize(maxBytes)}.`;
  }
  if (code === 'unsupported_type') {
    return `"${fileName}" não é um formato de vídeo suportado. Use MP4, WebM ou MOV.`;
  }
  if (code === 'invalid_video') {
    return `"${fileName}" não parece ser um vídeo. Confira se o arquivo não está corrompido ou renomeado.`;
  }
  if (status === 429) {
    return 'Muitos envios em pouco tempo. Aguarde alguns minutos antes de tentar de novo.';
  }
  if (status !== undefined && status >= 500) {
    return `O servidor falhou ao processar "${fileName}". Se repetir, avise o time.`;
  }

  return `Não foi possível enviar "${fileName}".`;
}
